import { Row, Section } from "anketa-core/group";
import { MessageCollection, Translation } from "anketa-core/i18n";
import {
  AnketaCheckPermissionOptions,
  AnketaResourcesEnum,
  baseMacs,
  calculatePrivateMacAddresses,
  getAgents,
  getVpcValues,
  Vpc,
} from "../../../../";
import { calculateHostIP } from "../../../../helper/ip";
import { cpeHalfFor, cpeSlotFor } from "./cpe-half";
import { OpenQuestion } from "anketa-core/open_question";
import { OptionsQuestion } from "anketa-core/options_question";
import { NumericValidator, TextValidator } from "anketa-core/validation";
import { TextValidatorType } from "anketa-core/base";
import { QuestionsRightsTypes } from "..";
import {
  type CardBadge,
  presentAsCard,
  setFieldGroup,
} from "../../../components/presentation";

const BADGE_REMOVED = [
  new Translation("en", "Removed"),
  new Translation("de", "Entfernt"),
];
const BADGE_ROOM_A = [
  new Translation("en", "Room A"),
  new Translation("de", "Raum A"),
];
const BADGE_ROOM_B = [
  new Translation("en", "Room B"),
  new Translation("de", "Raum B"),
];

// Field-group headings inside each node card.
const GROUP_STATUS = [
  new Translation("en", "Status"),
  new Translation("de", "Status"),
];
const GROUP_NETWORK = [
  new Translation("en", "Network"),
  new Translation("de", "Netzwerk"),
];
const GROUP_HARDWARE = [
  new Translation("en", "Hardware"),
  new Translation("de", "Hardware"),
];

/**
 * Room options for a two-room site.
 *
 * The keys ARE the stored fact values ("A"/"B"), not indices - unlike the
 * yes_no scale, where "0" means Yes. That matters because the parser and the
 * rendered values carry the letter through to `server.appliance[].zone`, and an
 * index would have to be translated back somewhere.
 */
export const ZONE_OPTIONS = "default.options.opsanio_zones";
MessageCollection.Global.setTranslations(`${ZONE_OPTIONS}.A`, [
  new Translation("en", "Room A"),
  new Translation("de", "Raum A"),
]);
MessageCollection.Global.setTranslations(`${ZONE_OPTIONS}.B`, [
  new Translation("en", "Room B"),
  new Translation("de", "Raum B"),
]);

const generateAgent = (
  offset: number,
  agentIdx: number,
  // Where in its CPE half this agent sits. Distinct from `offset`, which is the
  // agent's identity - slot number, MAC and transfer address all derive from
  // that and must not move when a node changes room.
  cpeSlot: number,
  vpcName: string,
  cpeCidr: string,
  transferCidr: string,
  questionsRights: (
    q: QuestionsRightsTypes,
    options?: AnketaCheckPermissionOptions<AnketaResourcesEnum.questionnaires>,
  ) => void,
  oldValues?: any,
  setDefaults?: boolean,
  defaultValues?: {
    agents: {
      id: string;
      macs: string[];
      redfishMac: string;
      serialNumber: string;
    }[];
    vpc: { vpcs: Vpc[]; tenantIds: number[] };
    vpcName: string;
  },
  customerGateway?: string,
  bgpEnabled?: boolean,
  zonesEnabled?: boolean,
): Row => {
  const hostname = `${vpcName}-a0-opsanio-00${agentIdx}`;
  const fqdn = `${hostname}.exanio.net`;
  const uid = `agent_${agentIdx}`;
  const existingValues = getAgents(oldValues).find(
    (old) => old.hostname === hostname,
  );
  let macRedfish: string = "";
  let macOne: string = "";
  let macTwo: string = "";
  let sn: string = "";
  let isRemoved: boolean = false;

  if (existingValues !== undefined) {
    macRedfish = existingValues.macs.redfish || "";
    macOne = existingValues.macs.one || "";
    macTwo = existingValues.macs.two || "";
    sn = existingValues.serialNumber || "";
    isRemoved = existingValues.removed;
  }

  const baseGroup = new Row(uid, [
    new Translation("en", isRemoved ? `${fqdn} (removed)` : fqdn),
    new Translation("de", isRemoved ? `${fqdn} (entfernt)` : fqdn),
  ]);

  // Physically pulling a node out of the rack must not renumber the ones that
  // stay: the slot (and with it the IP/MAC offsets derived from `agentIdx`) is
  // kept and flagged here instead. A flagged node drops out of
  // `server.appliance` and `common.counts.agents` in the rendered values, so the
  // charts scale to the nodes that really exist, and stops answering PXE and
  // NetBox lookups - its hardware record stays on file under
  // `server.removedAppliance`. To add a replacement, raise the agent count: that
  // appends a fresh slot rather than reusing this one.
  const removedField = new OptionsQuestion(
    `agent_${agentIdx}_removed`,
    [
      new Translation("en", "Node removed"),
      new Translation("de", "Node entfernt"),
    ],
    "default.options.yes_no",
  );
  removedField.help = [
    new Translation(
      "en",
      "Yes marks this node as physically decommissioned. It keeps its slot so the other nodes are not renumbered, but it is no longer deployed and no longer counts towards the node count. Add a replacement by raising the number of agents.",
    ),
    new Translation(
      "de",
      "Ja markiert diesen Node als ausgebaut. Er behält seinen Platz, damit die übrigen Nodes nicht neu nummeriert werden, wird aber nicht mehr ausgerollt und zählt nicht mehr zur Node-Anzahl. Einen Ersatz-Node fügt man über die Erhöhung der Agent-Anzahl hinzu.",
    ),
  ];
  questionsRights(removedField, {
    isInternal: true,
    isExternal: false,
  });
  // yes_no: "0" is Yes. Default every node to No so the flag is explicit in the
  // stored answers rather than relying on an absent value.
  removedField.setFactValue(isRemoved ? 0 : 1);

  // Which room this node stands in. A two-room site splits the CUSTOMER network
  // only - each room has its own customer CIDR and VLAN, and a node can only be
  // addressed on the one it is physically cabled to. CPE, transfer and exanio
  // stay cluster-wide, so nothing else keys on this.
  //
  // Only shown when "Unterschiedliche VLANs / Zonen" is answered Yes. Hidden
  // rather than absent: a single-room site still stores "A", so the answer is
  // explicit in the questionnaire instead of being inferred downstream from a
  // missing value, and turning the flag on later does not silently move
  // existing nodes.
  const zoneField = new OptionsQuestion(
    `agent_${agentIdx}_zone`,
    [
      new Translation("en", "Room / zone"),
      new Translation("de", "Raum / Zone"),
    ],
    ZONE_OPTIONS,
  );
  zoneField.help = [
    new Translation(
      "en",
      "The room this node is installed in. Selects which customer CIDR and VLAN it gets. Only relevant on a two-room site.",
    ),
    new Translation(
      "de",
      "Der Raum, in dem dieser Node installiert ist. Bestimmt, welches Kunden-CIDR und welches VLAN er erhält. Nur bei einer Zwei-Raum-Lösung relevant.",
    ),
  ];
  questionsRights(zoneField, { isInternal: true, isExternal: false });
  zoneField.setFactValue(existingValues?.zone === "B" ? "B" : "A");
  zoneField.visible = zonesEnabled === true;

  const firstCpeHostOffset = cpeSlot + 5;
  const cpeIp = cpeCidr ? calculateHostIP(cpeCidr, firstCpeHostOffset) : "";
  const redfishIp = cpeCidr
    ? calculateHostIP(cpeCidr, firstCpeHostOffset + 1)
    : "";
  const transferIp = transferCidr
    ? calculateHostIP(transferCidr, offset + 2)
    : "";

  const cpeIpField = new OpenQuestion(`agent_${agentIdx}_cpe`, [
    new Translation("en", "CPE IP address"),
    new Translation("de", "CPE IP-Adresse"),
  ]);
  questionsRights(cpeIpField);

  cpeIpField.setFactValue(cpeIp);
  cpeIpField.readonly = true;

  const redfishIpField = new OpenQuestion(`agent_${agentIdx}_redfish`, [
    new Translation("en", "Redfish IP address"),
    new Translation("de", "Redfish IP-Adresse"),
  ]);
  questionsRights(redfishIpField);

  redfishIpField.setFactValue(redfishIp);
  redfishIpField.readonly = true;

  const transferIpField = new OpenQuestion(`agent_${agentIdx}_transfer`, [
    new Translation("en", "Transfer IP address"),
    new Translation("de", "Transfer IP-Adresse"),
  ]);
  questionsRights(transferIpField);

  transferIpField.setFactValue(transferIp);
  transferIpField.readonly = true;

  const customerIpField = new OpenQuestion(`agent_${agentIdx}_customer`, [
    new Translation("en", "Customer IP address"),
    new Translation("de", "Kunden IP-Adresse"),
  ]);
  questionsRights(customerIpField);

  customerIpField.validator = new TextValidator();
  /* customerIpField.setFactValue(customerIp);
  customerIpField.readonly = true; */

  const serialnumber = new OpenQuestion(`agent_${agentIdx}_sn`, [
    new Translation("en", "Node Serial Number"),
    new Translation("de", "Seriennummer des Nodes"),
  ]);
  serialnumber.setFactValue(sn);
  questionsRights(serialnumber, {
    isInternal: true,
    isExternal: false,
  });
  serialnumber.validator = new TextValidator();

  const macAddressOne = new OpenQuestion(`agent_${agentIdx}_mac_one`, [
    new Translation("en", "CPE MAC-Address (First NIC)"),
    new Translation("de", "CPE MAC-Adresse (Erster NIC)"),
  ]);
  questionsRights(macAddressOne, {
    isInternal: true,
    isExternal: false,
  });
  macAddressOne.setFactValue(macOne);
  macAddressOne.validator = new TextValidator(TextValidatorType.MacAddress);

  const macAddressTwo = new OpenQuestion(`agent_${agentIdx}_mac_two`, [
    new Translation("en", "CPE MAC-Address (Second NIC)"),
    new Translation("de", "CPE MAC-Adresse (Zweiter NIC)"),
  ]);
  macAddressTwo.setFactValue(macTwo);

  questionsRights(macAddressTwo, {
    isInternal: true,
    isExternal: false,
  });

  macAddressTwo.validator = new TextValidator(TextValidatorType.MacAddress);

  const macAddressRedfish = new OpenQuestion(`agent_${agentIdx}_mac_redfish`, [
    new Translation("en", "CPE MAC-Address (Redfish)"),
    new Translation("de", "CPE MAC-Adresse (Redfish)"),
  ]);
  macAddressRedfish.setFactValue(macRedfish);
  questionsRights(macAddressRedfish, {
    isInternal: true,
    isExternal: false,
  });

  macAddressRedfish.validator = new TextValidator(TextValidatorType.MacAddress);

  const bgpSettingsRow = new Row("bgp_settings", [
    new Translation("en", "BGP Neighbor Configuration for " + hostname),
    new Translation("de", "BGP Neighbor Konfiguration für " + hostname),
  ]);

  if (bgpEnabled) {
    questionsRights(bgpSettingsRow, {
      isInternal: true,
    });
  } else {
    bgpSettingsRow.visible = false;
  }

  const bgpCidr = new OpenQuestion("bgp_cidr", [
    new Translation("en", "CPE CIDR"),
    new Translation("de", "CPE CIDR"),
  ]);
  // Computed, read-only. (Was a NumericValidator - a CIDR is not a number.)
  bgpCidr.readonly = true;
  bgpCidr.setFactValue(cpeCidr);
  bgpSettingsRow.addChild(bgpCidr);

  const validator = new NumericValidator();
  validator.min = 64512;
  validator.max = 65534;

  const localAs = new OpenQuestion("bgp_local_as", [
    new Translation("en", "Local AS"),
    new Translation("de", "Local AS"),
  ]);
  localAs.validator = validator;
  localAs.placeholder = [
    new Translation("en", "64512 - 65534"),
    new Translation("de", "64512 - 65534"),
  ];
  bgpSettingsRow.addChild(localAs);

  const as = new OpenQuestion("bgp_as", [
    new Translation("en", "Neighbor AS"),
    new Translation("de", "Neighbor AS"),
  ]);
  as.validator = validator;
  as.placeholder = localAs.placeholder;
  bgpSettingsRow.addChild(as);

  const bgpAddress = new OpenQuestion("bgp_ip", [
    new Translation("en", "Neighbor IP-Address"),
    new Translation("de", "Neighbor IP-Adresse"),
  ]);
  // An IPv4 address. (Was a NumericValidator, which only "passed" because
  // parseInt("10.0.0.1") is 10.)
  bgpAddress.validator = new TextValidator(TextValidatorType.IP);
  bgpSettingsRow.addChild(bgpAddress);
  const description = new OpenQuestion("bgp_description", [
    new Translation("en", "BGP Description"),
    new Translation("de", "BGP Beschreibung"),
  ]);
  // Free text. (Was a NumericValidator: the default "Customer Gateway" failed
  // as "not a number" on every node as soon as BGP was enabled.)
  description.validator = new TextValidator();
  bgpSettingsRow.addChild(description);

  // Card layout with labelled field groups. Only the ORDER of addChild changes
  // here - item ids, and with them the fact paths the parser reads
  // (agent_N.agent_N_*, agent_N.bgp_settings.bgp_*), stay as they were.
  // yes_no: "0" is Yes. Read live, so the header follows the answer.
  const nodeRemoved = (): boolean => removedField.fact.raw?.toString() === "0";
  presentAsCard(baseGroup, {
    collapsible: true,
    // A decommissioned node has nothing left to fill in - keep it folded.
    initialIsOpen: nodeRemoved() ? () => false : undefined,
    // Readable while collapsed: which box is this, and where does it answer.
    summary: () =>
      [serialnumber.fact.toString(), cpeIpField.fact.toString()]
        .filter((v) => v.trim() !== "")
        .join(" · "),
    badges: () => {
      const result: CardBadge[] = [];
      if (nodeRemoved()) {
        result.push({ label: BADGE_REMOVED, color: "warning" });
      }
      if (zonesEnabled === true) {
        result.push({
          label: zoneField.fact.raw?.toString() === "B" ? BADGE_ROOM_B : BADGE_ROOM_A,
          color: "hollow",
        });
      }
      return result;
    },
  });
  // BGP is folded until it needs input: with defaults set it is complete.
  presentAsCard(bgpSettingsRow, { collapsible: true });
  const groups: Array<
    [string, Translation[], Array<OpenQuestion | OptionsQuestion>]
  > = [
    ["status", GROUP_STATUS, [removedField, zoneField]],
    [
      "network",
      GROUP_NETWORK,
      [customerIpField, cpeIpField, redfishIpField, transferIpField],
    ],
    [
      "hardware",
      GROUP_HARDWARE,
      [serialnumber, macAddressOne, macAddressTwo, macAddressRedfish],
    ],
  ];
  groups.forEach(([key, label, fields]) => {
    fields.forEach((field) => {
      setFieldGroup(field, key, label);
      baseGroup.addChild(field);
    });
  });
  baseGroup.addChild(bgpSettingsRow);

  if (setDefaults) {
    const defaultAgent = defaultValues.agents[offset];
    macAddressOne.setFactValue(defaultAgent.macs[0]);
    macAddressTwo.setFactValue(defaultAgent.macs[1]);
    macAddressRedfish.setFactValue(defaultAgent.redfishMac);
    serialnumber.setFactValue(defaultAgent.serialNumber);
    as.setFactValue("123");
    bgpAddress.setFactValue(customerGateway);
    description.setFactValue("Customer Gateway");
    localAs.setFactValue("39366");
  }
  return baseGroup;
};

export const generateAgents = (
  agentCount: number,
  vpcName: string,
  cpeCidrA: string,
  cpeCidrB: string,
  transferCidr: string,
  questionsRights: (
    q: QuestionsRightsTypes,
    options?: AnketaCheckPermissionOptions<AnketaResourcesEnum.questionnaires>,
  ) => void,
  oldValues: any,
  setDefaults?: boolean,
  defaultValues?: {
    agents: {
      id: string;
      macs: string[];
      redfishMac: string;
      serialNumber: string;
    }[];
    vpc: { vpcs: Vpc[]; tenantIds: number[] };
    vpcName: string;
  },
  customerGateway?: string,
  bgpEnabled?: boolean,
  zonesEnabled?: boolean,
): Section => {
  const agents = new Section("agents", [
    new Translation("en", "Agents"),
    new Translation("de", "Agents"),
  ]);
  questionsRights(agents);
  agents.description = [
    new Translation(
      "en",
      `
The Agents appliance is installed directly on your premises.

All data in your environment remains exclusively on this appliance and never leaves your company.

This means that your information and data remain protected and under your control at all times.
      `,
    ),
    new Translation(
      "de",
      `
Die Appliance wird direkt bei Ihnen vor Ort installiert.

Sämtliche Daten Ihrer Umgebung verbleiben ausschließlich auf dieser Appliance und verlassen Ihr Unternehmen zu keinem Zeitpunkt.

So bleiben Ihre Informationen und Daten jederzeit geschützt und unter Ihrer Kontrolle.

        `,
    ),
  ];
  const cpeShared = calculateHostIP(cpeCidrA, 2);
  // Offset 0, not 1. Appliance offsets are 1-based, so the shared VIP was
  // handed a0-001's own address on BOTH vlans - a genuine duplicate on the
  // wire, with the switch flapping the MAC between the VIP holder and a0-001.
  // Zero is free: the base's last byte is 00 and no appliance ever claims it.
  const macShared = calculatePrivateMacAddresses(
    baseMacs,
    0,
    Number(getVpcValues(oldValues).vlan) || 0,
  );
  const cpeSharedRow = new Row("cpe_share", [
    new Translation("en", "CPE Shared"),
    new Translation("de", "CPE Shared"),
  ]);
  questionsRights(cpeSharedRow);

  const cpeSharedIp = new OpenQuestion(`agent_cpe_shared_ip`, [
    new Translation("en", "IP address"),
    new Translation("de", "IP-Adresse"),
  ]);
  questionsRights(cpeSharedIp);

  cpeSharedIp.setFactValue(cpeShared);
  cpeSharedIp.readonly = true;
  cpeSharedRow.addChild(cpeSharedIp);
  const cpeSharedMac = new OpenQuestion(`agent_cpe_shared_mac`, [
    new Translation("en", "MAC address"),
    new Translation("de", "MAC-Adresse"),
  ]);
  questionsRights(cpeSharedMac);

  cpeSharedMac.setFactValue(macShared[0]);
  cpeSharedMac.readonly = true;
  cpeSharedRow.addChild(cpeSharedMac);
  presentAsCard(cpeSharedRow);
  agents.addChild(cpeSharedRow);

  const customerSharedRow = new Row("customer_share", [
    new Translation("en", "Customer Shared"),
    new Translation("de", "Kunden Shared"),
  ]);
  questionsRights(customerSharedRow);

  const customerSharedIp = new OpenQuestion(`agent_customer_shared_ip`, [
    new Translation("en", "IP address"),
    new Translation("de", "IP-Adresse"),
  ]);
  questionsRights(customerSharedIp);

  /* customerSharedIp.setFactValue(customerShared);
  customerSharedIp.readonly = true; */
  customerSharedIp.validator = new TextValidator();

  customerSharedRow.addChild(customerSharedIp);
  const customerSharedMac = new OpenQuestion(`agent_customer_shared_mac`, [
    new Translation("en", "MAC address"),
    new Translation("de", "MAC-Adresse"),
  ]);
  questionsRights(customerSharedMac);

  customerSharedMac.setFactValue(macShared[1]);
  customerSharedMac.readonly = true;
  customerSharedRow.addChild(customerSharedMac);
  presentAsCard(customerSharedRow);
  agents.addChild(customerSharedRow);

  const numbers: Array<number> = [];
  if (typeof agentCount === "number" && vpcName && vpcName.trim() !== "") {
    for (let i = 1; i < agentCount + 1; i++) {
      numbers.push(i);
    }
    // Which CPE half an agent gets, and where inside it - see cpe-half.ts.
    // `offset` stays the agent's identity: its slot number, its MACs and its
    // transfer address all derive from that and must not move when a node
    // changes room. Only the CPE/Redfish pair follows the half.
    const zoneOfAgent = (agentIdx: number): string =>
      getAgents(oldValues).find(
        (old) => old.hostname === `${vpcName}-a0-opsanio-00${agentIdx}`,
      )?.zone === "B"
        ? "B"
        : "A";

    const filled: Record<string, number> = { A: 0, B: 0 };

    numbers.forEach((number) => {
      const offset = number - 1;
      const half = cpeHalfFor(
        zonesEnabled === true,
        zoneOfAgent(number),
        offset,
      );
      const cpeSlot = cpeSlotFor(half, filled[half]);
      filled[half] += 1;

      agents.addChild(
        generateAgent(
          offset,
          number,
          cpeSlot,
          vpcName,
          half === "B" ? cpeCidrB : cpeCidrA,
          transferCidr,
          questionsRights,
          oldValues,
          setDefaults,
          defaultValues,
          customerGateway,
          bgpEnabled,
          zonesEnabled,
        ),
      );
    });
  }

  return agents;
};
